import { useEffect, useState } from 'react';
import React from 'react';
import { useLocation, NavLink } from 'react-router-dom';
import PropTypes from 'prop-types';
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Icon from '@mui/material/Icon';
import Collapse from '@mui/material/Collapse';
import ArgonBox from 'components/ArgonBox';
import ArgonTypography from 'components/ArgonTypography';
import SidenavItem from 'examples/Sidenav/SidenavItem';
import SidenavFooter from 'examples/Sidenav/SidenavFooter';
import SidenavRoot from 'examples/Sidenav/SidenavRoot';
import sidenavLogoLabel from 'examples/Sidenav/styles/sidenav';
import { useArgonController, setMiniSidenav } from 'context';

function Sidenav({ color, brand, brandName, routes, ...rest }) {
  const [controller, dispatch] = useArgonController();
  const { miniSidenav, darkSidenav, layout } = controller;
  const location = useLocation();
  const { pathname } = location;
  const [openCollapse, setOpenCollapse] = useState({});

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  useEffect(() => {
    function handleMiniSidenav() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
    }
    window.addEventListener('resize', handleMiniSidenav);
    handleMiniSidenav();
    return () => window.removeEventListener('resize', handleMiniSidenav);
  }, [dispatch, location]);

  const handleCollapseClick = (key) => {
    setOpenCollapse((prevState) => ({
      ...prevState,
      [key]: !prevState[key],
    }));
  };

  const renderRoutes = routes.map(({ type, name, icon, title, key, href, route, collapse }) => {
    let returnValue;
    if (type === 'collapse') {
      returnValue = (
        <React.Fragment key={key}>
          <SidenavItem
            name={name}
            icon={icon}
            active={pathname.startsWith(route?.split('/')[1]) && route?.split('/')[1] === key}
            open={openCollapse[key]}
            onClick={() => handleCollapseClick(key)}
          />
          <Collapse in={openCollapse[key]} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {collapse.map((item) => {
                if (item.type === 'route') {
                  return (
                    <NavLink to={item.route} key={item.key}>
                      <SidenavItem name={item.name} icon={item.icon} active={pathname === item.route} nested={true} />
                    </NavLink>
                  );
                }
                return null;
              })}
            </List>
          </Collapse>
        </React.Fragment>
      );
    } else if (type === 'route') {
      if (href) {
        returnValue = (
          <Link href={href} key={key} target="_blank" rel="noreferrer">
            <SidenavItem name={name} icon={icon} active={pathname === route} />
          </Link>
        );
      } else {
        returnValue = (
          <NavLink to={route} key={key}>
            <SidenavItem name={name} icon={icon} active={pathname === route} />
          </NavLink>
        );
      }
    } else if (type === 'title') {
      returnValue = (
        <ArgonTypography
          key={key}
          color={darkSidenav ? 'white' : 'dark'}
          display="block"
          variant="caption"
          fontWeight="bold"
          textTransform="uppercase"
          opacity={0.6}
          pl={3}
          mt={2}
          mb={1}
          ml={1}
        >
          {title}
        </ArgonTypography>
      );
    } else if (type === 'divider') {
      returnValue = <Divider key={key} light={darkSidenav} />;
    }

    return returnValue;
  });

  return (
    <SidenavRoot {...rest} variant="permanent" ownerState={{ darkSidenav, miniSidenav, layout }}>
      <ArgonBox pt={3} pb={1} px={4} textAlign="center">
        <ArgonBox
          display={{ xs: 'block', xl: 'none' }}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          onClick={closeSidenav}
          sx={{ cursor: 'pointer' }}
        >
          <ArgonTypography variant="h6" color="secondary">
            <Icon sx={{ fontWeight: 'bold' }}>close</Icon>
          </ArgonTypography>
        </ArgonBox>
        <ArgonBox component={NavLink} to="/" display="flex" alignItems="center">
          {brand && <ArgonBox component="img" src={brand} alt="Argon Logo" width="2rem" mr={0.25} />}
          <ArgonBox width={!brandName && '100%'} sx={(theme) => sidenavLogoLabel(theme, { miniSidenav })}>
            <ArgonTypography
              component="h6"
              variant="button"
              fontWeight="medium"
              fontSize="18pt"
              color={darkSidenav ? 'white' : 'dark'}
            >
              {brandName}
            </ArgonTypography>
          </ArgonBox>
        </ArgonBox>
      </ArgonBox>
      <Divider light={darkSidenav} />
      <List>{renderRoutes}</List>
    </SidenavRoot>
  );
}

Sidenav.defaultProps = {
  color: 'info',
  brand: '',
};

Sidenav.propTypes = {
  color: PropTypes.oneOf(['primary', 'secondary', 'info', 'success', 'warning', 'error', 'dark']),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;
